import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from 'components/Accordion';
import Button from 'components/DesignSystem/Button';
import DatePicker from 'components/DatePicker';
import RadioButton from 'components/DesignSystem/RadioButton';
import CalendarioLegenda from 'components/Relatorios/shared/Aprovacao/CalendarioLegenda';
import useCalendarioAprovacao from 'hooks/Relatorios/Aprovacao/useCalendarioAprovacao';
import {
  selectIntersemanalDataAnalisada,
  selectIntersemanalCenario,
  setDataAnalisada,
  setCenario,
} from 'redux/reducers/relatoriosIntersemanalReducer';
import { getAprovacaoConfig } from 'helper/Relatorios/Aprovacao/config';
import { IntersemanalCenario } from 'types/Relatorios/Intersemanal';

import './index.scss';

const config = getAprovacaoConfig('intersemanal');

const CENARIOS: { value: IntersemanalCenario; label: string }[] = (
  Object.keys(config.variante.labels) as IntersemanalCenario[]
).map((value) => ({ value, label: config.variante.labels[value] }));

/**
 * Painel de controle do Informe Intersemanal. Espelha o `DecompControlPanel`:
 * `Accordion` do design system com `DatePicker` inline para a Data Analisada, mas
 * troca o campo de texto livre "Cenário" por radios fechados (Early/Preliminar/
 * POS_ONS, conforme o Figma) e não tem seção "Modo" (não existe esse conceito no
 * Intersemanal). Rascunho local (useState) só faz commit ao slice no clique em
 * "Atualizar Data"; a navegação por abas é imediata.
 *
 * Com a aprovação habilitada, o calendário ganha bolinhas (admin) ou só permite dias
 * publicados (cliente), consultando o **cenário aplicado**. D8: a data do rascunho
 * acompanha a data aplicada; o cenário do rascunho não é ressincronizado.
 */
export default function IntersemanalControlPanel() {
  const dispatch = useDispatch();
  const dataAnalisada = useSelector(selectIntersemanalDataAnalisada);
  const cenarioAplicado = useSelector(selectIntersemanalCenario);

  const [draftData, setDraftData] = useState(
    () => new Date(dataAnalisada.ano, dataAnalisada.mes - 1, dataAnalisada.dia),
  );
  const [draftCenario, setDraftCenario] = useState<IntersemanalCenario>(cenarioAplicado);

  const {
    datePickerProps, mostraLegenda, onDataSelecionada,
  } = useCalendarioAprovacao(config, dataAnalisada, cenarioAplicado);

  const { ano, mes, dia } = dataAnalisada;
  useEffect(() => {
    setDraftData((atual) => (
      atual.getFullYear() === ano && atual.getMonth() === mes - 1 && atual.getDate() === dia
        ? atual
        : new Date(ano, mes - 1, dia)
    ));
  }, [ano, mes, dia]);

  const handleDataChange = useCallback((date: Date | null) => {
    if (!date) return;
    setDraftData(date);
    onDataSelecionada(date);
  }, [onDataSelecionada]);

  const handleCenarioChange = useCallback((value: IntersemanalCenario) => () => {
    setDraftCenario(value);
  }, []);

  const handleAtualizar = useCallback(() => {
    dispatch(setDataAnalisada({
      mes: draftData.getMonth() + 1,
      ano: draftData.getFullYear(),
      dia: draftData.getDate(),
    }));
    dispatch(setCenario(draftCenario));
  }, [dispatch, draftData, draftCenario]);

  return (
    <div className="intersemanal-control-panel">
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>Data Analisada</Accordion.Header>
          <Accordion.Body className="d-grid">
            <DatePicker
              inline
              selected={draftData}
              onChange={handleDataChange}
              maxDate={new Date()}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...datePickerProps}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>Cenário</Accordion.Header>
          <Accordion.Body className="d-grid gap-1">
            {CENARIOS.map(({ value, label }) => (
              <RadioButton
                key={value}
                name="intersemanal-cenario"
                label={label}
                value={value}
                checked={draftCenario === value}
                onChange={handleCenarioChange(value)}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Button onClick={handleAtualizar} className="intersemanal-control-panel__atualizar">
        Atualizar Data
      </Button>

      {mostraLegenda && <CalendarioLegenda isAdmin />}
    </div>
  );
}
